import {DotFillIcon} from '@primer/octicons-react'
import {memo} from 'react'
import type {SessionState} from '../types/session'
import {getLeadingVisualIconAttributes} from '../utils/get-leading-visual-icon-attributes'

export interface TaskLeadingVisualProps {
  isOptimistic?: boolean
  prState?: string
  prInMergeQueue?: boolean
  sessionState: SessionState
  isArchived?: boolean
  isCLI?: boolean
  className?: string
}

/**
 * Displays a leading visual icon for a task/session based on its current state.
 *
 * This component is memoized to prevent animated icons (like CheckSpinner) from resetting
 * when other tasks in a list update.
 */
export const TaskLeadingVisual = memo(function TaskLeadingVisual({
  isOptimistic = false,
  prState,
  prInMergeQueue,
  sessionState,
  isArchived,
  isCLI,
  className,
}: TaskLeadingVisualProps) {
  const iconAttributes = isOptimistic
    ? {icon: DotFillIcon, color: 'var(--fgColor-attention)', 'data-testid': 'icon-dot'}
    : getLeadingVisualIconAttributes(prState, prInMergeQueue, sessionState, isArchived, isCLI)

  const Icon = iconAttributes.icon

  return (
    <div
      className={className}
      style={{
        color: iconAttributes.color,
        // Add margin-top for spinning icon to align properly
        marginTop: sessionState === 'in_progress' && !isOptimistic && !isCLI ? '3px' : undefined,
      }}
      data-testid={iconAttributes['data-testid']}
    >
      <Icon />
    </div>
  )
})
